import React, { useMemo } from 'react';
import { defineMessages, useIntl, MessageDescriptor } from 'react-intl';
import {
  TableModule,
  TableConfiguration,
  TableModuleProps,
} from '@lifeomic/chroma-react/components/TableModule';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { makeStyles, useTheme } from '@lifeomic/chroma-react/styles';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import {
  CheckCircle,
  MapPin,
  Video2,
  Loader,
  Slash,
} from '@lifeomic/chromicons';
import { findCoding, findExtension } from '@lifeomic/fhir-constants';
import moment from 'moment';
import { fhirExtensions, fhirSystems } from '../fhir-systems';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import { MemberOnboardingData } from '../index';

const messages = defineMessages({
  provider: {
    id: 'OnboardingSnapshotTable.provider',
    defaultMessage: 'Provider',
  },
  name: {
    id: 'OnboardingSnapshotTable.name',
    defaultMessage: 'Name',
  },
  nextAppointment: {
    id: 'OnboardingSnapshotTable.nextAppointment',
    defaultMessage: 'Next Appointment',
  },
  noUpcomingAppointments: {
    id: 'OnboardingSnapshotTable.noUpcomingAppointments',
    defaultMessage: 'No Upcoming Appointments',
  },
  inPerson: {
    id: 'OnboardingSnapshotTable.inPerson',
    defaultMessage: 'In-Person',
  },
  virtual: {
    id: 'OnboardingSnapshotTable.virtual',
    defaultMessage: 'Virtual',
  },
  goToPatient: {
    id: 'OnboardingSnapshotTable.goToPatient',
    defaultMessage: 'Go To Patient',
  },
  consents: {
    id: 'OnboardingSnapshotTable.consents',
    defaultMessage: 'Consents',
  },
  forms: {
    id: 'OnboardingSnapshotTable.forms',
    defaultMessage: 'Forms',
  },
  nursePrescreening: {
    id: 'OnboardingSnapshotTable.nursePrescreening',
    defaultMessage: 'Prescreening',
  },
  ppc: {
    id: 'OnboardingSnapshotTable.ppc',
    defaultMessage: 'PPC',
  },
  testing: {
    id: 'OnboardingSnapshotTable.testing',
    defaultMessage: 'Testing',
  },
  lastAssignedAndCompleted: {
    id: 'OnboardingSnapshotTable.lastAssignedAndCompleted',
    defaultMessage:
      'Last Assigned {lastAssigned} • Last Completed {lastCompleted}',
  },
  lastCompleted: {
    id: 'OnboardingSnapshotTable.lastCompleted',
    defaultMessage: 'Last Completed {lastCompleted}',
  },
  lastAssigned: {
    id: 'OnboardingSnapshotTable.lastAssigned',
    defaultMessage: 'Last Assigned {lastAssigned}',
  },
  noneAssigned: {
    id: 'OnboardingSnapshotTable.noneAssigned',
    defaultMessage: 'None Assigned in the last 13 months',
  },
});

const useStyles = makeStyles(theme => ({
  table: {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  headerCell: {
    maxWidth: theme.pxToRem(125),
    lineHeight: 1.25,
    textAlign: 'center',
    whiteSpace: 'normal',
  },
  name: {
    lineHeight: 1.35,
    textTransform: 'capitalize',
  },
  subtext: {
    color: theme.palette.text.hint,
    fontSize: theme.pxToRem(10),
    letterSpacing: theme.pxToRem(0.6),
    lineHeight: theme.pxToRem(10),
  },
  locationIcon: {
    color: theme.palette.text.hint,
    minWidth: theme.pxToRem(20),
    minHeight: theme.pxToRem(20),
    width: theme.pxToRem(20),
    height: theme.pxToRem(20),
  },
  apptText: {
    lineHeight: 1.35,
  },
  centerAlign: {
    textAlign: 'center',
  },
  nameCell: {
    maxWidth: theme.pxToRem(175),
  },
  statusIcon: {
    minWidth: theme.pxToRem(20),
    minHeight: theme.pxToRem(20),
    width: theme.pxToRem(20),
    height: theme.pxToRem(20),
  },
}));

const hasAppointmentReason = ({
  appointment,
  system,
  code,
}: {
  appointment: WithPlatformDefaults<fhir3.Appointment>;
  system: string;
  code: string;
}) =>
  appointment.start &&
  findCoding(appointment.meta.tag, {
    system,
    code,
  });

const isPast = (appointment?: WithPlatformDefaults<fhir3.Appointment>) =>
  moment(appointment?.start).isSameOrBefore(moment());

const isUpcoming = (appointment?: WithPlatformDefaults<fhir3.Appointment>) =>
  moment(appointment?.start).isSameOrAfter(moment());

export type Snapshots = {
  consents: {
    status: QuestionnaireResponseSnapshotProps['status'];
    hint: {
      message: MessageDescriptor;
      lastAssigned: string | undefined;
      lastCompleted: string | undefined;
    };
  };
  surveys: {
    status: QuestionnaireResponseSnapshotProps['status'];
    hint: {
      message: MessageDescriptor;
      lastAssigned: string | undefined;
      lastCompleted: string | undefined;
    };
  };
  prescreening: {
    status: AppointmentSnapshotProps['status'];
    appointment: WithPlatformDefaults<fhir3.Appointment> | undefined;
    date: string | undefined;
  };
  ppc: {
    status: AppointmentSnapshotProps['status'];
    appointment: WithPlatformDefaults<fhir3.Appointment> | undefined;
    date: string | undefined;
  };
  diagnostics: {
    status: AppointmentSnapshotProps['status'];
    appointment: WithPlatformDefaults<fhir3.Appointment> | undefined;
    date: string | undefined;
  };
  nextAppointment: {
    appointment: WithPlatformDefaults<fhir3.Appointment> | undefined;
  };
};

// Format appointment statuses
const getAppointmentStatus = (
  appointment: WithPlatformDefaults<fhir3.Appointment> | undefined,
  {
    surveyStatus,
  }: { surveyStatus?: QuestionnaireResponseSnapshotProps['status'] } = {},
) => {
  if (isUpcoming(appointment)) {
    return 'tentative';
  }

  // Only consider survey status if it is provided
  if (surveyStatus && surveyStatus === 'positive' && isPast(appointment)) {
    return 'complete';
  } else if (isPast(appointment)) {
    return 'complete';
  }

  return 'in-jeopardy';
};

const getValidWindowsPerQuestionnaireType = (_options: {
  patient: WithPlatformDefaults<fhir3.Patient>;
}): {
  consents: {
    value: number;
    unit: moment.unitOfTime.Base;
  };
  surveys: {
    value: number;
    unit: moment.unitOfTime.Base;
  };
} => {
  // Currently the general rule is that consents _must_ be completed within 30
  // days of the next diagnostics appointment. And, surveys done once a year
  // That is the default, however, if that changed it can be easily done here or
  // if it was conditional on the patient's tier or appointment type etc, that
  // could be done here.
  return {
    consents: { value: 30, unit: 'days' },
    surveys: {
      value: 1,
      unit: 'year',
    },
  };
};

const getConsentStatus = ({
  consents,
  lastCompletedConsent,
  diagnosticsAppointment,
  range,
}: {
  consents: WithPlatformDefaults<fhir3.Consent>[];
  lastCompletedConsent: fhir3.Consent | undefined;
  diagnosticsAppointment: WithPlatformDefaults<fhir3.Appointment> | undefined;
  range: {
    value: number;
    unit: moment.unitOfTime.Base;
  };
}): QuestionnaireResponseSnapshotProps['status'] => {
  const status =
    consents.some(consent => consent.status !== 'active') ||
    consents.length === 0
      ? 'negative'
      : 'positive';

  // Consents typically must be completed only 30 days before the
  // next testing date. If not, they must be re-assigned and completed
  if (
    status === 'positive' &&
    lastCompletedConsent &&
    isValidDate(lastCompletedConsent.dateTime) &&
    diagnosticsAppointment &&
    isValidDate(diagnosticsAppointment.start)
  ) {
    const lastCompletedConsentDate = moment(lastCompletedConsent.dateTime);
    const appointmentDate = moment(diagnosticsAppointment.start);
    const windowStart = moment(appointmentDate).subtract(
      range.value,
      range.unit,
    );

    if (
      !lastCompletedConsentDate.isBetween(
        windowStart,
        appointmentDate,
        'day',
        '[]',
      )
    ) {
      return 'negative';
    }
  }

  return status;
};

const getSurveyStatus = ({
  surveyResponses,
  lastCompletedSurvey,
  diagnosticsAppointment,
  range,
}: {
  surveyResponses: Array<WithPlatformDefaults<fhir3.QuestionnaireResponse>>;
  lastCompletedSurvey:
    | WithPlatformDefaults<fhir3.QuestionnaireResponse>
    | undefined;
  diagnosticsAppointment: WithPlatformDefaults<fhir3.Appointment> | undefined;
  range: {
    value: number;
    unit: moment.unitOfTime.Base;
  };
}): QuestionnaireResponseSnapshotProps['status'] => {
  const surveyMinimalStatus =
    surveyResponses.every(response =>
      findExtension(response.extension, {
        prop: 'valueBoolean',
        url: fhirSystems.questionnaireResponse.notStarted.system,
      }),
    ) || surveyResponses.length === 0
      ? 'negative'
      : surveyResponses.some(response => response.status === 'in-progress')
      ? 'warning'
      : 'positive';

  if (
    surveyMinimalStatus === 'positive' &&
    lastCompletedSurvey &&
    isValidDate(lastCompletedSurvey?.authored) &&
    diagnosticsAppointment &&
    isValidDate(diagnosticsAppointment.start)
  ) {
    const lastCompletedSurveyDate = moment(lastCompletedSurvey.authored);
    const appointmentDate = moment(diagnosticsAppointment.start);
    const windowStart = moment(appointmentDate).subtract(
      range.value,
      range.unit,
    );

    if (
      !lastCompletedSurveyDate.isBetween(
        windowStart,
        appointmentDate,
        'day',
        '[]',
      )
    ) {
      return 'negative';
    }
  }

  return surveyMinimalStatus;
};

const isValidDate = (date: string | undefined): date is string => {
  return !isNaN(new Date(date || '').getTime());
};

const sortDateByLastOccurrence = (a: Date, b: Date) => {
  return b.getTime() - a.getTime();
};

const getLastOccurrence = <
  T extends Array<WithPlatformDefaults<fhir3.Resource>>,
>(
  items: T,
  getDate: (item: T[number]) => string,
): T[number] | undefined => {
  const [lastOccurrence] = items
    .filter(item => {
      const date = getDate(item);
      return isValidDate(date);
    })
    .sort((a, b) =>
      sortDateByLastOccurrence(new Date(getDate(a)), new Date(getDate(b))),
    );

  return lastOccurrence;
};

const getCreatedDate = (item: { extension: fhir3.Extension[] } | undefined) => {
  return (
    findExtension(item?.extension, {
      url: fhirExtensions.createdDate.url,
      prop: 'valueDateTime',
    })?.valueDateTime || ''
  );
};

const buildHint = ({
  lastAssigned,
  lastCompleted,
}: {
  lastAssigned: string | undefined;
  lastCompleted: string | undefined;
}): {
  message: MessageDescriptor;
  lastAssigned: string | undefined;
  lastCompleted: string | undefined;
} => {
  const hasLastAssigned = Boolean(lastAssigned);
  const hasLastCompleted = Boolean(lastCompleted);

  let message: MessageDescriptor;

  if (hasLastAssigned && hasLastCompleted) {
    message = messages.lastAssignedAndCompleted;
  } else if (hasLastAssigned) {
    message = messages.lastAssigned;
  } else if (hasLastCompleted) {
    message = messages.lastCompleted;
  } else {
    message = messages.noneAssigned;
  }

  return {
    message,
    lastAssigned: lastAssigned
      ? new Date(lastAssigned).toLocaleDateString()
      : undefined,
    lastCompleted: lastCompleted
      ? new Date(lastCompleted).toLocaleDateString()
      : undefined,
  };
};

export const formatSnapshots = ({
  patient,
  consents,
  surveyResponses,
  keyAppointments,
  appointments,
}: Omit<MemberOnboardingData, 'snapshots'>): Snapshots => {
  const [prescreeningAppointment] = keyAppointments.filter(appointment =>
    hasAppointmentReason({
      appointment,
      system: fhirSystems.appointment.reason.system,
      code: fhirSystems.appointment.reason.values.prescreening,
    }),
  );

  const [ppcAppointment] = keyAppointments.filter(appointment =>
    hasAppointmentReason({
      appointment,
      system: fhirSystems.appointment.reason.system,
      code: fhirSystems.appointment.reason.values.ppc,
    }),
  );

  const [diagnosticsAppointment] = keyAppointments.filter(appointment =>
    hasAppointmentReason({
      appointment,
      system: fhirSystems.appointment.reason.system,
      code: fhirSystems.appointment.reason.values.diagnostics,
    }),
  );

  const validRange = getValidWindowsPerQuestionnaireType({
    patient: patient,
  });

  // Format consents status
  const lastCompletedConsent = getLastOccurrence(
    consents.filter(consent => consent.status === 'active'),
    consent => consent.dateTime || '',
  );
  const consentStatus = getConsentStatus({
    consents,
    lastCompletedConsent,
    diagnosticsAppointment,
    range: validRange.consents,
  });

  // Format survey responses status
  const lastCompletedSurvey = getLastOccurrence(
    surveyResponses.filter(response => response.status === 'completed'),
    response => response.authored || '',
  );
  const surveyStatus = getSurveyStatus({
    surveyResponses,
    lastCompletedSurvey,
    diagnosticsAppointment,
    range: validRange.surveys,
  });

  return {
    consents: {
      status: consentStatus,
      hint: buildHint({
        lastAssigned: getCreatedDate(
          getLastOccurrence(consents, getCreatedDate),
        ),
        lastCompleted: lastCompletedConsent?.dateTime,
      }),
    },
    surveys: {
      status: surveyStatus,
      hint: buildHint({
        lastAssigned: getCreatedDate(
          getLastOccurrence(surveyResponses, getCreatedDate),
        ),
        lastCompleted: lastCompletedSurvey?.authored,
      }),
    },
    prescreening: {
      status: getAppointmentStatus(prescreeningAppointment, {
        surveyStatus,
      }),
      appointment: prescreeningAppointment,
      date: prescreeningAppointment?.start,
    },
    ppc: {
      status: getAppointmentStatus(ppcAppointment),
      appointment: ppcAppointment,
      date: ppcAppointment?.start,
    },
    diagnostics: {
      status: getAppointmentStatus(diagnosticsAppointment),
      appointment: diagnosticsAppointment,
      date: diagnosticsAppointment?.start,
    },
    nextAppointment: {
      appointment: appointments[0],
    },
  };
};

type OnboardingSnapshotTableProps = {
  data: MemberOnboardingData[];
  isLoading: boolean;
  rowActions?: TableModuleProps['rowActions'];
};

export const OnboardingSnapshotTable: React.FC<
  OnboardingSnapshotTableProps
> = ({ data, isLoading, rowActions }) => {
  const styles = useStyles();
  const { formatMessage } = useIntl();
  const tableRef = React.useRef<HTMLTableElement>(null);

  const tableConfig = useMemo<TableConfiguration[]>(() => {
    return [
      {
        isSticky: true,
        header: {
          className: styles.nameCell,
          label: formatMessage(messages.name),
        },
        cell: {
          className: styles.nameCell,
          content: ({ member }: MemberOnboardingData) => {
            return (
              <Box direction="column" gap={0.25}>
                <Text className={styles.name} size="caption">
                  {member.name}
                </Text>
                {member.tier && (
                  <Box align="center" gap={0.25}>
                    <Text className={styles.subtext}>
                      {member.tier.toUpperCase().replace('-V2', '')}
                    </Text>
                  </Box>
                )}
              </Box>
            );
          },
        },
      },
      {
        isSticky: true,
        header: {
          label: formatMessage(messages.provider),
        },
        cell: {
          content: ({ member }: MemberOnboardingData) => {
            return (
              <Box direction="column" gap={0.25}>
                <Text className={styles.name} size="caption">
                  {member.providerName}
                </Text>
                <Box align="center" gap={0.25}>
                  <Text className={styles.subtext}>
                    {member.careCoordinatorName}
                  </Text>
                </Box>
              </Box>
            );
          },
        },
      },
      {
        isSticky: true,
        header: {
          label: formatMessage(messages.nextAppointment),
        },
        cell: {
          content: ({ snapshots }: MemberOnboardingData) => {
            const nextAppointment = formatUpcomingAppointment(
              snapshots.nextAppointment.appointment,
            );

            if (!nextAppointment) {
              return formatMessage(messages.noUpcomingAppointments);
            }

            return (
              <Tooltip
                title={`${
                  nextAppointment.description
                    ? `${nextAppointment.description} - `
                    : ''
                } ${
                  nextAppointment.locationTypeCode === 'in-person'
                    ? formatMessage(messages.inPerson)
                    : formatMessage(messages.virtual)
                }`}
              >
                <Box align="center" gap={1.5}>
                  {nextAppointment.locationIcon && (
                    <nextAppointment.locationIcon
                      className={styles.locationIcon}
                    />
                  )}
                  <Box direction="column">
                    <Text className={styles.apptText} size="caption">
                      {nextAppointment.date}
                    </Text>
                    {nextAppointment.time && (
                      <Text className={styles.apptText} size="caption">
                        {nextAppointment.time}
                      </Text>
                    )}
                  </Box>
                </Box>
              </Tooltip>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.consents),
          className: styles.headerCell,
        },
        cell: {
          className: styles.centerAlign,
          content: ({ snapshots }: MemberOnboardingData) => {
            return (
              <Tooltip
                title={formatMessage(snapshots.consents.hint.message, {
                  lastAssigned: snapshots.consents.hint.lastAssigned,
                  lastCompleted: snapshots.consents.hint.lastCompleted,
                })}
              >
                <Box justify="center">
                  <QuestionnaireResponseSnapshot
                    status={snapshots.consents.status}
                  />
                </Box>
              </Tooltip>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.forms),
          className: styles.headerCell,
        },
        cell: {
          className: styles.centerAlign,
          content: ({ snapshots }: MemberOnboardingData) => {
            return (
              <Tooltip
                title={formatMessage(snapshots.consents.hint.message, {
                  lastAssigned: snapshots.consents.hint.lastAssigned,
                  lastCompleted: snapshots.consents.hint.lastCompleted,
                })}
              >
                <Box justify="center">
                  <QuestionnaireResponseSnapshot
                    status={snapshots.surveys.status}
                  />
                </Box>
              </Tooltip>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.nursePrescreening),
          className: styles.headerCell,
        },
        cell: {
          className: styles.centerAlign,
          content: ({ snapshots }: MemberOnboardingData) => {
            return (
              <Box justify="center">
                <AppointmentSnapshot
                  type="prescreening"
                  status={snapshots.prescreening.status}
                  appointment={snapshots.prescreening.appointment}
                />
              </Box>
            );
          },
        },
      },
      {
        header: {
          className: styles.headerCell,
          label: formatMessage(messages.ppc),
        },
        cell: {
          className: styles.centerAlign,
          content: ({ snapshots }: MemberOnboardingData) => {
            return (
              <Box justify="center">
                <AppointmentSnapshot
                  type="ppc"
                  status={snapshots.ppc.status}
                  appointment={snapshots.ppc.appointment}
                />
              </Box>
            );
          },
        },
      },
      {
        header: {
          label: formatMessage(messages.testing),
          className: styles.headerCell,
        },
        cell: {
          className: styles.centerAlign,
          content: ({ snapshots }: MemberOnboardingData) => {
            return (
              <Box justify="center">
                <AppointmentSnapshot
                  type="diagnostics"
                  status={snapshots.diagnostics.status}
                  appointment={snapshots.diagnostics.appointment}
                />
              </Box>
            );
          },
        },
      },
    ];
  }, [
    formatMessage,
    styles.apptText,
    styles.centerAlign,
    styles.headerCell,
    styles.locationIcon,
    styles.name,
    styles.nameCell,
    styles.subtext,
  ]);

  return (
    <TableModule
      data={data}
      config={tableConfig}
      ref={tableRef}
      isLoading={isLoading}
      wrapCellContent
      rowActions={data && data.length > 0 ? rowActions : undefined}
      className={styles.table}
    />
  );
};

type QuestionnaireResponseSnapshotProps = {
  status: 'positive' | 'warning' | 'negative';
};

export const QuestionnaireResponseSnapshot: React.FC<
  QuestionnaireResponseSnapshotProps
> = ({ status }) => {
  const theme = useTheme();
  const styles = useStyles();

  const { StatusIcon, color } = {
    positive: {
      StatusIcon: CheckCircle,
      color: theme.palette.text.hint,
    },
    warning: {
      StatusIcon: Loader,
      color: theme.palette.text.hint,
    },
    negative: {
      StatusIcon: Slash,
      color: theme.palette.negative.main,
    },
  }[status] || {
    StatusIcon: () => null,
    color: '',
  };

  return <StatusIcon color={color} className={styles.statusIcon} />;
};

type AppointmentSnapshotProps = {
  appointment?: WithPlatformDefaults<fhir3.Appointment>;
  status: 'tentative' | 'complete' | 'in-jeopardy';
  type: keyof typeof fhirSystems.appointment.reason.values;
  direction?: 'row' | 'column';
};

export const AppointmentSnapshot: React.FC<AppointmentSnapshotProps> = ({
  appointment,
  type,
  status,
  direction = 'column',
}) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const styles = useStyles();
  const purpose = {
    prescreening: formatMessage(messages.nursePrescreening),
    ppc: formatMessage(messages.ppc),
    diagnostics: formatMessage(messages.testing),
  }[type];

  if (!appointment?.start) {
    return (
      <Slash
        className={styles.statusIcon}
        color={theme.palette.negative.main}
      />
    );
  }

  const date = moment(appointment.start).format('M/D/YYYY');
  const statusIcon = {
    tentative: (
      <Loader className={styles.statusIcon} color={theme.palette.text.hint} />
    ),
    complete: (
      <CheckCircle
        className={styles.statusIcon}
        color={theme.palette.text.hint}
      />
    ),
    'in-jeopardy': (
      <Slash
        className={styles.statusIcon}
        color={theme.palette.negative.main}
      />
    ),
  }[status];

  return (
    <Tooltip title={purpose}>
      <Box
        direction={direction}
        align="center"
        gap={direction === 'column' ? 0.25 : 1}
      >
        {statusIcon}
        <Text className={styles.apptText} size="caption">
          {date}
        </Text>
      </Box>
    </Tooltip>
  );
};

export const formatUpcomingAppointment = (
  appointment: WithPlatformDefaults<fhir3.Appointment> | undefined,
) => {
  if (!appointment) {
    return;
  }

  const date = appointment?.start
    ? moment(appointment.start).format('M/D/YYYY')
    : '—';
  const time = appointment?.start
    ? moment(appointment.start).format('h:mmA')
    : '';

  const locationTypeCode = findCoding(appointment?.meta?.tag, {
    system: fhirSystems.appointment.locationType.system,
  })?.code;

  const description =
    findCoding(appointment?.meta?.tag, {
      system: fhirSystems.appointment.elationReason.system,
    })?.code ||
    appointment.description ||
    '';

  const locationIcon = {
    'in-person': MapPin,
    virtual: Video2,
  }[locationTypeCode || ''];

  return {
    date,
    time,
    locationTypeCode,
    description,
    locationIcon,
  };
};
